import { Col, Divider, Form, Row } from 'antd';
import { Rule } from 'antd/es/form';
import moment from 'moment';
import { openErrorNoti } from 'presentation/components/Notification';
import { FormCheckBox } from 'presentation/components/form/FormCheckBox.component';
import { FormInput } from 'presentation/components/form/FormInput.component';
import { FormSelect } from 'presentation/components/form/FormSelect.component';
import { FormTextArea } from 'presentation/components/form/FormTextArea.component';
import { completeFetching, startFetching } from 'presentation/redux/features/app/appSlice';
import { useAppDispatch } from 'presentation/redux/hooks';
import { PRIVATE_ROUTES } from 'presentation/router/CONSTANTS';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MASTER_CATEGORY_LIST } from 'utilities/constants/COMMON';
import { DATE_FORMAT } from 'utilities/constants/DATE';
import { commonHelpers } from 'utilities/helpers';
import { NUMBER_MULTIPLE_DEPOSIT } from '../../CU-015-01';
import { ItemBox } from '../../CU-015-01/components/ItemBox';
import { StepProps } from '../props/StepProps';
import { ButtonList } from './ButtonList';

export const PurchaseStepTwo = ({ data, masterData, setIsChange, ...props }: StepProps) => {
    const [btnDisable, setBtnDisable] = useState<boolean>(true);
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [finalPayment, setFinalPayment] = useState<any>(0);
    const [showOrther, setShowOrther] = useState<boolean>(false);

    useEffect(() => {
        props.scrollTop && props.scrollTop();
    }, []);

    useEffect(() => {
        if (data) {
            form.setFieldsValue({
                ...data,
                deposit: data.deposit || '1',
            });
            onChangeForm();
        }
        const deposit = commonHelpers.toNumberOnly(data?.deposit) || 1;
        const depositNumber =
            (isNaN(Number(deposit)) ? 0 : Number(deposit)) * NUMBER_MULTIPLE_DEPOSIT;
        const finalPayment = subtract(data?.totalPrice, depositNumber);
        setFinalPayment(finalPayment);
    }, [data]);

    const depositNotZeroValidator: Rule = {
        // カスタム validator
        validator: (_rule, value) => {
            const raw = value ?? '';
            const n = Number(String(raw).replace(/[^0-9.-]/g, ''));
            if (Number.isNaN(n)) {
            return Promise.reject(new Error('数値で入力してください'));
            }
            if (n === 0) {
            return Promise.reject(new Error('0以上で入力してください'));
            }
            if (n < 0) {
            return Promise.reject(new Error('0より大きい数を入力してください'));
            }
            return Promise.resolve();
        },
    };
    const onChangeForm = () => {
        form.validateFields({
            validateOnly: true,
        })
            .then(() => {
                setBtnDisable(false);
            })
            .catch((err) => {
                setBtnDisable(err.errorFields?.length > 0);
            });
    };

    const submitNext = async () => {
        try {
            dispatch(startFetching());
            const depositValue = commonHelpers.toNumberOnly(form.getFieldValue('deposit')) ?? 0;
            const depositNumber =
                (isNaN(Number(depositValue)) ? 0 : Number(depositValue)) * NUMBER_MULTIPLE_DEPOSIT;
            const payload = {
                ...data,
                ...form.getFieldsValue(),
                dealPurposeOthers: showOrther ? form.getFieldValue('dealPurposeOthers') : '',
                deposit: depositNumber,
                finalPayment: finalPayment,
                scheduledContractDate: oneWeekAfterFormat(),
                scheduledDepositPaymentDate: oneWeekAfterFormat(),
                scheduledFinalPaymentDate: twoMonthAfterFormat(),
                purchasePriceExcludingBrokerageFee: subtract(
                    data?.purchasePriceIncludingBrokerageFee,
                    data?.brokerageFeeSpecificTreat,
                ),
                procedureId: data?.procedureId,
                purchaseOfferId: data?.purchaseOfferId,
                agreementOpeDiv01: 10,
                agreementOpeDiv02: 10,
            };
            const response = await props.purchaseUseCase.savePurchaseCompanyStep2(payload);
            if (response) {
                const param = {
                    listedPropertyId: data?.listedPropertyId,
                    purchasePrice: data?.purchasePrice,
                    purchaseOfferId: data?.purchaseOfferId,
                    procedureId: data?.procedureId,
                    loanUseStatus: data?.loanUseStatus,
                };
                navigate(
                    `${PRIVATE_ROUTES.PATH}/${PRIVATE_ROUTES.SUB.CU_016_02}?${commonHelpers.generateQueryString(param)}`,
                );
            }
        } catch (error: any) {
            openErrorNoti();
        } finally {
            dispatch(completeFetching());
        }
    };

    useEffect(() => {
        props.scrollTop && props.scrollTop();
    }, []);

    const changeBtnDisable = (isError: any) => {
        if (isError) {
            setBtnDisable(true);
        } else {
            onChangeForm();
        }
    };

    const oneWeekAfter = () => {
        const date = new Date();
        date.setDate(date.getDate() + 7);
        return date;
    };

    const twoMonthAfter = () => {
        const date = oneWeekAfter();
        date.setMonth(date.getMonth() + 2);
        const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);
        return lastDay;
    };

    const oneWeekAfterFormat = () => {
        return moment(oneWeekAfter()).format(DATE_FORMAT.FORMAT_FULL);
    };

    const twoMonthAfterFormat = () => {
        return moment(twoMonthAfter()).format(DATE_FORMAT.FORMAT_FULL);
    };

    const subtract = (a?: any, b?: any) => {
        const aNumber = isNaN(Number(a)) ? 0 : Number(a);
        const bNumber = isNaN(Number(b)) ? 0 : Number(b);
        return aNumber - bNumber;
    };

    return (
        <>
            <Form
                form={form}
                onValuesChange={() => {
                    onChangeForm();
                    setIsChange && setIsChange(true);
                }}
                onFinish={submitNext}
            >
                <Row className="min-w-min">
                    <div className="w-full mb-[32px]">
                        <span className="content-step-form label-step-form">
                            契約予定日前後でメッセージから予定日を設定させていただきます。
                        </span>
                        <Row>
                            <Col span={12} className="pr-[12px]">
                                <ItemBox
                                    label="契約予定日"
                                    value={data?.scheduleContactDate}
                                    type="date"
                                />
                            </Col>
                        </Row>
                    </div>
                    <div className="w-full mb-[32px]">
                        <span className="content-step-form heading-step-form">売買代金</span>
                        <Divider className="mb-6 my-1" />
                        <Row>
                            <Col span={12} className="pr-[12px]">
                                <ItemBox
                                    label="購入価格"
                                    value={data?.purchasePriceExcludingBrokerageFee}
                                    type="number"
                                    suffix="円"
                                    className="mb-[12px]"
                                    // roundFunction={(value: any) => {
                                    //     if (value) {
                                    //         return commonHelpers.roundTenThousandYen(value) * 10000;
                                    //     }
                                    //     return value;
                                    // }}
                                />
                            </Col>
                            <Col span={12} className="pr-[12px]">
                                <ItemBox
                                    label="仲介手数料"
                                    value={data?.brokerageFeeSpecificTreat ?? ''}
                                    type="number"
                                    suffix="円"
                                    className="mb-[12px]"
                                    defaultvalue={0}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col span={12} className="pr-[12px]">
                                <ItemBox
                                    label="代金総額"
                                    value={data?.totalPrice}
                                    type="number"
                                    suffix="円"
                                    className="mb-[12px]"
                                />
                            </Col>
                            <Col span={12} className="pr-[12px]">
                                <ItemBox
                                    label="自己資金"
                                    value={data?.personalFunds}
                                    type="number"
                                    suffix="円"
                                    className="mb-[12px]"
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col span={12} className="pr-[12px]">
                                <FormInput
                                    label="手付金"
                                    name="deposit"
                                    maxLength={7}
                                    formatNumber={false}
                                    type="number"
                                    size={'large'}
                                    suffix={'00,000円'}
                                    thousandSuffix="1"
                                    inputClassName="text-right"
                                    required
                                    labelClassName="content-step-form label-step-form"
                                    rules={[depositNotZeroValidator]}
                                    onChange={(e) => {
                                        const deposit = commonHelpers.toNumberOnly(e) ?? 0;
                                        const depositNumber =
                                            (isNaN(Number(deposit)) ? 0 : Number(deposit)) *
                                            NUMBER_MULTIPLE_DEPOSIT;
                                        const finalPayment = subtract(
                                            data?.totalPrice,
                                            depositNumber,
                                        );
                                        setFinalPayment(finalPayment);
                                    }}
                                />
                            </Col>
                            <Col span={12} className="pr-[12px]">
                                <ItemBox
                                    label="手付金振込予定日"
                                    value={data?.scheduleContactDate}
                                    type="date"
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col span={12} className="pr-[12px]">
                                <ItemBox
                                    label="最終金"
                                    value={finalPayment}
                                    type="number"
                                    suffix="円"
                                />
                            </Col>
                            <Col span={12} className="pr-[12px]">
                                <ItemBox
                                    label="最終金振込予定日"
                                    value={data?.scheduleFinalPaymentDate}
                                    type="date"
                                />
                            </Col>
                        </Row>
                        {/* <Row className="mt-[12px]">
                            <Col span={24} className="pr-[12px]">
                                <FormCheckBox
                                    name="includeBrokerageFee"
                                    disabled
                                    defaultValue={data?.includeBrokerageFee}
                                    className="mb-0"
                                    options={[
                                        {
                                            value: 1,
                                            label: '仲介手数料を購入価格に組み込む。',
                                        },
                                    ]}
                                />
                            </Col>
                        </Row> */}
                    </div>
                    <div className="w-full mb-[32px]">
                        <Row gutter={[0, 12]} className="my-[12px]">
                            <Col span={4} className="pr-[12px]">
                                <span className="content-step-form heading-step-form">
                                    賃貸管理
                                </span>
                            </Col>
                        </Row>
                        <Divider className="mb-6 my-1" />
                        <Row gutter={[0, 12]}>
                            <Col span={24}>
                                <FormCheckBox
                                    name="confirm_cic"
                                    className="mb-0"
                                    required
                                    options={[
                                        {
                                            value: '10',
                                            label: '株式会社シイ・アイ・シーに賃貸管理業務を委託することに同意します。',
                                        },
                                    ]}
                                    showErrorMessage={false}
                                />
                            </Col>
                        </Row>
                    </div>

                    <div className="w-full mb-[32px]">
                        <span className="content-step-form heading-step-form">取引目的</span>
                        <Divider className="mb-6 my-1" />
                        <Row gutter={[0, 12]} className="my-[12px]">
                            <Col span={24}>
                                <FormSelect
                                    options={
                                        masterData?.[
                                            MASTER_CATEGORY_LIST.DEAL_PURPOSE_TYPE_1_CLASSIFICATION
                                        ]
                                    }
                                    label="取引目的"
                                    required
                                    name="dealPurposeType"
                                    size={'large'}
                                    labelClassName="content-step-form label-step-form"
                                    onChange={(value) => {
                                        setShowOrther(value == '99');
                                    }}
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]} className="my-[12px]">
                            <Col span={24}>
                                <FormTextArea
                                    label="その他目的"
                                    name="dealPurposeOthers"
                                    required={showOrther}
                                    disabled={!showOrther}
                                    maxLength={1000}
                                    currentForm={form}
                                    onValidateError={changeBtnDisable}
                                    size={'large'}
                                    cols={5}
                                    labelClassName="content-step-form label-step-form"
                                    style={{ fontSize: 14 }}
                                />
                            </Col>
                        </Row>
                        <Row gutter={[0, 12]}>
                            <Col span={24}>
                                <FormInput
                                    label="キャンペーンコード（任意）"
                                    name="campaignCode"
                                    maxLength={20}
                                    size={'large'}
                                    onlyRomanji={true}
                                    labelClassName="content-step-form label-step-form"
                                />
                            </Col>
                        </Row>
                    </div>

                    <ButtonList {...props} next={submitNext} disable={btnDisable} />
                </Row>
            </Form>
        </>
    );
};
